<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  watch,
  nextTick,
  ref,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { BaseButton } from '@magma-app/magma-lapilli';
import useVuelidate from '@vuelidate/core';

// Components
import Header from '@/components/views/ambassador-signup/header.vue';
import HeaderStart from '@/components/views/ambassador-signup/header-start.vue';

// Composables
import { useAmbassadorSignupFlow } from '@/components/ambassador-signup/composables/useAmbassadorSignupFlow';
import { useAmbassadorSignupValidation } from '@/components/ambassador-signup/config/ambassador-signup-validation';
import { useTurnstileAlert } from '@/composables/useTurnstileAlert';

const bottomOffset = ref(0);
const currentStepRef = ref<{
  executeTurnstile?: () => Promise<string>;
  resetTurnstile?: () => void;
} | null>(null);
const isTurnstileEnabled = Boolean((import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim());
const isHandlingContinue = ref(false);
const { showTurnstileErrorAlert } = useTurnstileAlert();

onMounted(() => {
  if (window.visualViewport) {
    const handler = () => {
      // différence entre viewport height et layout height
      const offset = window.innerHeight - (window.visualViewport?.height || 0);
      bottomOffset.value = offset > 0 ? offset : 0;
    };

    window.visualViewport.addEventListener('resize', handler);
    handler();

    onBeforeUnmount(() => {
      window.visualViewport?.removeEventListener('resize', handler);
    });
  }
});

// Types (none needed currently)

// Main onboarding flow logic
const {
  currentStep,
  formData,
  disableNext,
  codeValidationResult,
  currentStepConfig,
  canProceed,
  canShowBackButton,
  goBack,
  goForward,
  currentData,
  isLoading,
  isCampaignLoading,
} = useAmbassadorSignupFlow();

// Validation
const validationRules = useAmbassadorSignupValidation();

// Only apply external results for code validation when on verification step and user has interacted
const externalResults = computed(() => {
  if (
    currentStep.value === 'verification-phone' &&
    Object.keys(codeValidationResult.value).length > 0 &&
    v$.value.code.$dirty
  ) {
    return codeValidationResult;
  }
  return {};
});

const v$ = useVuelidate(validationRules, formData, {
  $externalResults: externalResults,
});

// Lazy load step components for better performance
const stepComponents = {
  Welcome: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/welcome.vue')
  ),
  Start: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/start.vue')
  ),
  Share: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/share.vue')
  ),
  AppLanguages: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/app-languages.vue')
  ),
  Email: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/email.vue')
  ),
  Confirm: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/confirm.vue')
  ),
  AlreadyExist: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/already-exist.vue')
  ),
  Phone: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/phone.vue')
  ),
  VerificationPhone: defineAsyncComponent(
    () =>
      import('@/components/views/ambassador-signup/steps/verification-phone.vue')
  ),
  Name: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/name.vue')
  ),
  Profile: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/profile.vue')
  ),
  PostalAddress: defineAsyncComponent(
    () =>
      import('@/components/views/ambassador-signup/steps/postal-address.vue')
  ),
  Actions: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/actions.vue')
  ),
  TouchPoints: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/touchpoints.vue')
  ),
  Resume: defineAsyncComponent(
    () => import('@/components/views/ambassador-signup/steps/resume.vue')
  ),
};

// Current step component
const currentComponent = computed(() => {
  const config = currentStepConfig.value;
  if (!config) return null;
  return stepComponents[config.component as keyof typeof stepComponents];
});

// Step-specific props
const stepProps = computed(() => {
  switch (currentStep.value) {
    case 'welcome':
      return {
        images: currentData.value?.helperProfilePictures || [],
      };

    case 'share':
      return {
        organizationName: currentData.value?.organizationName || '',
      };

    case 'email': {
      // For pre-filled emails from campaign data, only show validation if user has actually typed something different
      const isPreFilledEmail =
        !!currentData.value?.email &&
        formData.value.email === currentData.value.email;
      return {
        modelValue: formData.value.email,
        validation:
          v$.value.email.$dirty && !isPreFilledEmail
            ? v$.value.email
            : undefined,
        termsAccepted: formData.value.termsAccepted,
        termsUrl: currentData.value?.organizationTermsUrl || '',
        disableEmail: !!currentData.value?.email,
      };
    }

    case 'phone':
      return {
        modelValue: formData.value.phoneNumber,
        validation: v$.value.phoneNumber.$dirty
          ? v$.value.phoneNumber
          : undefined,
      };

    case 'verification-phone':
      return {
        modelValue: formData.value.code,
        validation: v$.value.code.$dirty ? v$.value.code : undefined,
      };

    case 'name':
      return {
        modelValue: {
          firstname: formData.value.firstname,
          lastname: formData.value.lastname,
        },
        validationFirstname: v$.value.firstname.$dirty
          ? v$.value.firstname
          : undefined,
        validationLastname: v$.value.lastname.$dirty
          ? v$.value.lastname
          : undefined,
      };

    case 'profile':
      return {
        modelValue: {
          profilePicture: formData.value.profilePicture,
          position: formData.value.position,
          languagesSpoken: formData.value.languagesSpoken,
        },
        pictureUrl: formData.value.profilePictureUrl,
        validationProfilePicture: v$.value.profilePicture,
        validationPosition: v$.value.position,
        validationLanguagesSpoken: v$.value.languagesSpoken,
      };

    case 'address':
      return {
        modelValue: {
          address: formData.value.address,
          googlePlaceId: formData.value.googlePlaceId,
        },
        validationAddress: v$.value.address,
      };

    case 'actionsInterestedIn':
      return {
        modelValue: {
          actionsInterestedIn: formData.value.actionsInterestedIn,
        },
        validationActionsInterestedIn: v$.value.actionsInterestedIn,
      };

    case 'touchPoints':
      return {
        modelValue: {
          userTouchPoints: formData.value.userTouchPoints,
        },
        touchPoints: currentData.value?.touchPoints,
        validation: v$.value.userTouchPoints,
      };

    case 'language':
      return {
        modelValue: {
          language: formData.value.language,
        },
      };

    case 'resume':
      return {
        firstname: formData.value.firstname,
        lastname: formData.value.lastname,
        position: formData.value.position,
        userTouchPoints: formData.value.userTouchPoints,
        profilePicture:
          formData.value.profilePictureUrlMedium ||
          formData.value.profilePictureUrl ||
          '',
        spokenLanguages: formData.value.languagesSpoken,
        actionsInterestedIn: formData.value.actionsInterestedIn,
      };

    default:
      return {};
  }
});

// Step event handlers
const handleStepUpdate = (value: unknown) => {
  switch (currentStep.value) {
    case 'language': {
      const languageValue = value as { language: string };
      formData.value.language = languageValue.language;
      formData.value.isLanguageSetByUser = true;
      break;
    }
    case 'email':
      if (typeof value === 'string') {
        formData.value.email = value;
      } else if (value && typeof value === 'object') {
        const emailValue = value as { email?: string; termsAccepted?: boolean };
        if (typeof emailValue.email === 'string') {
          formData.value.email = emailValue.email;
        }
        if (typeof emailValue.termsAccepted === 'boolean') {
          formData.value.termsAccepted = emailValue.termsAccepted;
        }
      }
      break;

    case 'phone':
      formData.value.phoneNumber = value as string;
      break;

    case 'verification-phone':
      formData.value.code = value as string;
      break;

    case 'name': {
      const nameValue = value as { firstname?: string; lastname?: string };
      if (nameValue.firstname !== undefined) {
        formData.value.firstname = nameValue.firstname;
        v$.value.lastname.$reset();
      }
      if (nameValue.lastname !== undefined) {
        formData.value.lastname = nameValue.lastname;
        v$.value.firstname.$reset();
      }
      break;
    }

    case 'profile': {
      const profileValue = value as {
        profilePicture?: File | null;
        position?: string;
        languagesSpoken?: string[];
      };
      if (profileValue.profilePicture !== undefined) {
        formData.value.profilePicture = profileValue.profilePicture;
      }
      if (profileValue.position !== undefined) {
        formData.value.position = profileValue.position;
      }
      if (profileValue.languagesSpoken !== undefined) {
        formData.value.languagesSpoken = profileValue.languagesSpoken;
      }
      break;
    }

    case 'address': {
      const addressValue = value as {
        address: string;
        googlePlaceId?: string | null;
      };
      formData.value.address = addressValue.address;
      if (addressValue.googlePlaceId !== undefined) {
        formData.value.googlePlaceId = addressValue.googlePlaceId || '';
      }
      break;
    }

    case 'actionsInterestedIn': {
      const actionsValue = value as {
        actionsInterestedIn: Array<
          'matching' | 'ugc' | 'event' | 'challenge' | 'referral' | 'review'
        >;
      };
      formData.value.actionsInterestedIn = actionsValue.actionsInterestedIn;
      break;
    }

    case 'touchPoints': {
      const touchpointsValue = value as {
        userTouchPoints: Array<{ touchPointId: number; value: string }>;
      };
      formData.value.userTouchPoints = touchpointsValue.userTouchPoints;
      break;
    }
  }
};

// Handle profile picture upload event
const handlePictureUploaded = ({profilePictureUrl,
  profilePictureUrlMedium}: {
  profilePictureUrl: string;
  profilePictureUrlMedium: string;
}) => {
  // The composable should have already updated both URLs, but let's ensure the main URL is set
  formData.value.profilePictureUrl = profilePictureUrl;
  formData.value.profilePictureUrlMedium = profilePictureUrlMedium;
};

// Touchpoint handlers (for backward compatibility)
const handleResendCode = async () => {
  // This will be handled by the verification step component
  // through the onboarding flow
};

// Clear validation errors when form data changes
v$.value.$clearExternalResults();
v$.value.$validate();

// Reset validation dirty state when entering email, verification-phone, or name steps
// This prevents validation errors from showing before user starts typing
watch(currentStep, () => {
  nextTick(() => {
    v$.value.email.$reset();
    v$.value.code.$reset();
    v$.value.firstname.$reset();
    v$.value.lastname.$reset();
  });
});

const handleContinue = async () => {
  if (isHandlingContinue.value) {
    return;
  }

  isHandlingContinue.value = true;
  const shouldExecuteTurnstile =
    currentStep.value === 'email' && isTurnstileEnabled;
  const activeStepRef = currentStepRef.value;

  if (shouldExecuteTurnstile) {
    try {
      const turnstileToken = await activeStepRef?.executeTurnstile?.();
      if (!turnstileToken) {
        throw new Error('TURNSTILE-TOKEN-MISSING');
      }
      formData.value.turnstileToken = turnstileToken;
    } catch {
      formData.value.turnstileToken = undefined;
      activeStepRef?.resetTurnstile?.();
      showTurnstileErrorAlert();
      isHandlingContinue.value = false;
      return;
    }
  }

  try {
    await goForward();
  } finally {
    if (shouldExecuteTurnstile) {
      formData.value.turnstileToken = undefined;
      activeStepRef?.resetTurnstile?.();
    }
    isHandlingContinue.value = false;
  }
};
</script>

<template>
  <div
    v-if="!isCampaignLoading"
    class="content-form flex bg-background-subtle-hover min-h-[100svh] w-full"
  >
    <div
      class="flex-1 flex flex-col md:flex-none md:w-[500px] lg:!w-[500px] xl:!w-[600px] md:mx-auto bg-white md:border md:border-border-subtle md:h-fit md:rounded-2xl md:mt-10 xl:mt-20 md:shadow-lg relative overflow-hidden md:mb-20 min-h-[100svh] md:min-h-0"
    >
      <!-- Header for main flow steps -->
      <Header
        v-if="currentStepConfig?.hasHeader"
        :step="currentStep"
        :can-show-back-button="canShowBackButton"
        @back="goBack"
      />

      <!-- Header for intro steps -->
      <HeaderStart
        v-if="currentStepConfig?.hasStartHeader"
        :organization-logo="currentData?.organizationLogoUrl || ''"
        :organization-name="currentData?.organizationName || 'Learnico'"
      />

      <!-- Step content -->
      <div
        :class="`w-full py-5 px-5 flex-1 overflow-y-auto ${
          currentStepConfig?.hasHeader ? 'mt-16' : ''
        } ${currentStepConfig?.hasContinueButton ? 'pb-20 md:pb-5' : ''}`"
      >
        <component
          :is="currentComponent"
          ref="currentStepRef"
          v-bind="stepProps"
          @update:model-value="handleStepUpdate"
          @picture-uploaded="handlePictureUploaded"
          @resend-code="handleResendCode"
          @go-back="goBack"
        />
      </div>

      <!-- Spacer -->
      <div v-if="!currentStepConfig?.hasProgressDots" class="flex-none" />

      <!-- Continue button section -->
      <div
        v-if="currentStepConfig?.hasContinueButton"
        :class="`w-full bg-white shadow-md 
                 sticky bottom-0 md:static md:border-0 md:shadow-none pb-safe
                 ${
                   currentStepConfig?.hasProgressDots
                     ? 'px-5 lg:px-20 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-4 md:pb-10 md:pt-0'
                     : 'px-5 py-[calc(0.75rem+env(safe-area-inset-bottom))] md:py-10'
                 }`"
        :style="{ bottom: `${bottomOffset}px` }"
      >
        <!-- Progress dots for intro steps -->
        <div
          v-if="currentStepConfig?.hasProgressDots"
          class="flex-1 flex items-end justify-center gap-2 pb-4 md:pb-6"
        >
          <div
            :class="`w-2 h-2 rounded-full ${
              currentStep === 'welcome'
                ? 'bg-background-brand-emphasis'
                : 'bg-background-brand-disabled'
            }`"
          />
          <div
            :class="`w-2 h-2 rounded-full ${
              currentStep === 'start'
                ? 'bg-background-brand-emphasis'
                : 'bg-background-brand-disabled'
            }`"
          />
          <div
            :class="`w-2 h-2 rounded-full ${
              currentStep === 'share'
                ? 'bg-background-brand-emphasis'
                : 'bg-background-brand-disabled'
            }`"
          />
        </div>

        <!-- Continue button -->
        <BaseButton
          size="lg"
          class="w-full"
          :disabled="!canProceed || disableNext || isLoading || isHandlingContinue"
          @click="handleContinue"
        >
          {{ $t('generic.continue') }}
        </BaseButton>
      </div>
    </div>
  </div>

  <!-- Loading state -->
  <div
    v-else
    class="content-form flex bg-background-subtle-hover h-dvh w-full items-center justify-center"
  >
    <div class="text-center">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-background-brand-emphasis mx-auto mb-4"
      />
      <p class="text-text-subtle">{{ $t('generic.loading') }}</p>
    </div>
  </div>
</template>

<style scoped>
/* Mobile keyboard handling */
@media (max-width: 768px) {
  .content-form {
    /* Use small viewport height to account for mobile keyboards */
    min-height: 100svh;
  }

  /* Ensure inputs scroll into view when focused on mobile */
  .content-form input,
  .content-form textarea,
  .content-form select {
    scroll-margin-bottom: 120px;
  }
}

/* Add any specific styling for the wizard container if needed */
</style>
